import { getProfessionPathwayChoices, PROFESSION_CATEGORY_LABELS, PROFESSIONS, searchProfessions, type ProfessionCategory, type ProfessionCatalogEntry } from "../data/professionalTaxonomy";
import { type ProfessionalCredentialUploadInput, type ProfessionalMemberCredential, type ProfessionalRequestSummary } from "../lib/api";

type DraftMode = "new" | "correction" | "addition";
export interface CredentialDraft {
  id: string;
  qualificationPath: ProfessionCategory | null;
  credentialType: string;
  credentialNumber: string;
  issuer: string;
  expiryDate: string;
  evidence: File[];
}
export interface Draft {
  id: string;
  professionTitle: string;
  entry: ProfessionCatalogEntry | null;
  mode: DraftMode;
  requestId?: string;
  credentials: CredentialDraft[];
}

export const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  revoked: "bg-slate-100 text-slate-700",
} as const;
export const MAX_CREDENTIALS_PER_REQUEST = 3;
export const MAX_EVIDENCE_IMAGES = 3;
export const MAX_EVIDENCE_BYTES_PER_IMAGE = 10 * 1024 * 1024;
const MAX_EVIDENCE_BYTES_PER_REQUEST = 30 * 1024 * 1024;
export const ALLOWED_EVIDENCE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ALL_PATHWAY_CHOICES = PROFESSIONS.flatMap(getProfessionPathwayChoices);
const NON_REGULATED_PROOF_SUGGESTIONS = ["Portfolio", "Work samples", "Employment record", "Client reference"];
const NON_REGULATED_ISSUER_SUGGESTIONS = ["Employer", "Client", "Professional association", "Self"];
// Preview data is restricted to development so example requests never replace a member's real requests.
export const PREVIEW_REQUESTS_ENABLED = import.meta.env.DEV && new URLSearchParams(window.location.search).has("previewProfessionalRequests");
export const PREVIEW_REQUESTS: ProfessionalRequestSummary[] = [
  { id: "preview-pending", professionTitle: "Cloud Professional", status: "pending", decisionNote: null, credentialCount: 1, createdAt: "2026-09-24T00:00:00.000Z", updatedAt: "2026-09-29T00:00:00.000Z" },
  { id: "preview-approved", professionTitle: "Software Developer", status: "approved", decisionNote: null, credentialCount: 2, createdAt: "2026-09-18T00:00:00.000Z", updatedAt: "2026-09-26T00:00:00.000Z" },
  { id: "preview-rejected", professionTitle: "Professional Teacher", status: "rejected", decisionNote: "Please upload a clearer image of your registration document.", credentialCount: 1, createdAt: "2026-09-20T00:00:00.000Z", updatedAt: "2026-09-27T00:00:00.000Z" },
];
function previewEvidence(id: string, createdAt: string) {
  return { id, contentType: "image/png" as const, sizeBytes: 1024, createdAt };
}
export const PREVIEW_CREDENTIALS: Record<string, ProfessionalMemberCredential[]> = {
  "preview-pending": [{ id: "preview-cloud-proof", credentialType: "AWS Certification", qualificationPath: "certificationAccreditation", credentialNumber: null, issuer: "Amazon Web Services", issueDate: null, expiryDate: null, status: "pending", decisionNote: null, createdAt: "2026-09-24T00:00:00.000Z", images: [previewEvidence("preview-cloud-image", "2026-09-24T00:00:00.000Z")] }],
  "preview-approved": [
    { id: "preview-portfolio", credentialType: "Portfolio", qualificationPath: "nonRegulatedOccupation", credentialNumber: null, issuer: "Self", issueDate: null, expiryDate: null, status: "approved", decisionNote: null, createdAt: "2026-09-18T00:00:00.000Z", images: [previewEvidence("preview-portfolio-image", "2026-09-18T00:00:00.000Z")] },
    { id: "preview-reference", credentialType: "Client reference", qualificationPath: "nonRegulatedOccupation", credentialNumber: null, issuer: "Client", issueDate: null, expiryDate: null, status: "approved", decisionNote: null, createdAt: "2026-09-21T00:00:00.000Z", images: [previewEvidence("preview-reference-image", "2026-09-21T00:00:00.000Z")] },
  ],
  "preview-rejected": [{ id: "preview-teacher-proof", credentialType: "Registration document", qualificationPath: "registrationWithoutExamination", credentialNumber: null, issuer: "PRC", issueDate: null, expiryDate: null, status: "rejected", decisionNote: "The document image is unreadable.", createdAt: "2026-09-20T00:00:00.000Z", images: [previewEvidence("preview-teacher-image", "2026-09-20T00:00:00.000Z")] }],
};

export function makePreviewEvidenceBlob(): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = 720;
  canvas.height = 440;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Could not show sample evidence."));
  context.fillStyle = "#f2f6fb";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "#c7d5e6";
  context.strokeRect(24, 24, canvas.width - 48, canvas.height - 48);
  context.fillStyle = "#123250";
  context.textAlign = "center";
  context.font = "bold 34px sans-serif";
  context.fillText("SAMPLE EVIDENCE", canvas.width / 2, 205);
  context.font = "20px sans-serif";
  context.fillText("Preview only · no personal document", canvas.width / 2, 245);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not show sample evidence.")), "image/png"));
}

function uniqueSuggestions(values: (string | null)[]): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

// [verification.qualification-paths](../../../docs/product/profiles-and-verification.md#verification-qualification-paths): catalog rows suggest proof and issuer text within a path; members may still enter their actual evidence details.
export function proofSuggestions(entry: ProfessionCatalogEntry | null, category: ProfessionCategory | null): string[] {
  if (!category) return [];
  if (category === "nonRegulatedOccupation") return NON_REGULATED_PROOF_SUGGESTIONS;
  const local = entry ? getProfessionPathwayChoices(entry).filter((choice) => choice.category === category) : [];
  const all = ALL_PATHWAY_CHOICES.filter((choice) => choice.category === category);
  return uniqueSuggestions([...local, ...all].flatMap((choice) => [
    /certification|certificate|accreditation/i.test(choice.designation ?? "") ? choice.designation : null,
    choice.credentialType,
  ]));
}

export function issuerSuggestions(entry: ProfessionCatalogEntry | null, category: ProfessionCategory | null, proof: string): string[] {
  if (!category) return [];
  if (category === "nonRegulatedOccupation") return NON_REGULATED_ISSUER_SUGGESTIONS;
  const local = entry ? getProfessionPathwayChoices(entry).filter((choice) => choice.category === category) : [];
  const all = ALL_PATHWAY_CHOICES.filter((choice) => choice.category === category);
  const choices = [...local, ...all];
  const normalizedProof = proof.trim().toLocaleLowerCase("en");
  // A matching proof narrows the issuer prompt; otherwise show same-path issuers without asserting that any one issued it.
  const matching = normalizedProof
    ? choices.filter((choice) => [choice.designation, choice.credentialType].some((value) => value?.toLocaleLowerCase("en") === normalizedProof))
    : [];
  return uniqueSuggestions((matching.length > 0 ? matching : choices).map((choice) => choice.issuingAuthority));
}

export function findProfession(professionTitle: string): ProfessionCatalogEntry | null {
  const normalized = professionTitle.trim().toLocaleLowerCase("en");
  return PROFESSIONS.find((entry) => entry.profession.toLocaleLowerCase("en") === normalized) ?? null;
}

export function qualificationPathsFor(entry: ProfessionCatalogEntry | null): ProfessionCategory[] {
  // [verification.qualification-paths](../../../docs/product/profiles-and-verification.md#verification-qualification-paths): known professions expose their catalog paths, including alternatives; custom titles retain all five choices.
  if (!entry) return Object.keys(PROFESSION_CATEGORY_LABELS) as ProfessionCategory[];
  return Array.from(new Set(getProfessionPathwayChoices(entry).map((choice) => choice.category)));
}

export function makeDraft(
  professionTitle: string,
  entry: ProfessionCatalogEntry | null,
  mode: DraftMode = "new",
  requestId?: string,
  priorCredential?: ProfessionalMemberCredential,
): Draft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    professionTitle,
    entry,
    mode,
    requestId,
    credentials: [makeCredentialDraft(entry, priorCredential)],
  };
}

export function makeCredentialDraft(entry: ProfessionCatalogEntry | null, priorCredential?: ProfessionalMemberCredential): CredentialDraft {
  const paths = qualificationPathsFor(entry);
  const priorPath = priorCredential?.qualificationPath ?? null;
  // [verification.qualification-paths](../../../docs/product/profiles-and-verification.md#verification-qualification-paths): corrections retain an allowed prior path; a historical null path stays unselected unless this catalog title has only one choice.
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    qualificationPath: priorPath && paths.includes(priorPath) ? priorPath : entry && paths.length === 1 ? paths[0] : null,
    credentialType: priorCredential?.credentialType ?? "",
    credentialNumber: priorCredential?.credentialNumber ?? "",
    issuer: priorCredential?.issuer ?? "",
    expiryDate: priorCredential?.expiryDate ?? "",
    evidence: [],
  };
}

export function makeCredentialInput(draft: CredentialDraft): ProfessionalCredentialUploadInput {
  if (!draft.qualificationPath) throw new Error("Choose a qualification path for each credential.");
  return {
    qualificationPath: draft.qualificationPath,
    credentialType: draft.credentialType.trim(),
    credentialNumber: draft.credentialNumber.trim() || null,
    issuer: draft.issuer.trim(),
    expiryDate: draft.expiryDate || null,
    evidence: draft.evidence,
  };
}

/** Client readiness only; the member API remains authoritative for admission. */
export function isDraftReady(draft: Draft): boolean {
  return (
    draft.professionTitle.trim().length > 0
    && draft.professionTitle.trim().length <= 160
    && draft.credentials.length >= 1
    && draft.credentials.length <= 3
    && draft.credentials.every((credential) => (
      credential.qualificationPath !== null
      && credential.credentialType.trim().length > 0
      && credential.credentialType.trim().length <= 120
      && credential.issuer.trim().length > 0
      && credential.issuer.trim().length <= 160
      && credential.credentialNumber.trim().length <= 80
      && credential.evidence.length >= 1
      && credential.evidence.length <= MAX_EVIDENCE_IMAGES
      && credential.evidence.every((file) => ALLOWED_EVIDENCE_TYPES.has(file.type) && file.size > 0 && file.size <= MAX_EVIDENCE_BYTES_PER_IMAGE)
    ))
    && draft.credentials.reduce((total, credential) => total + credential.evidence.reduce((sum, file) => sum + file.size, 0), 0) <= MAX_EVIDENCE_BYTES_PER_REQUEST

  );
}

export function professionSuggestions(query: string, selectedTitles: ReadonlySet<string>): ProfessionCatalogEntry[] {
  return query.trim() ? searchProfessions(query)
    : PROFESSIONS.filter(entry => !selectedTitles.has(entry.profession.toLocaleLowerCase("en"))).slice(0, 3);
}
